import { WEBSOCKET_URL } from '../api/config'

export enum WebSocketReadyState {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

export interface WebSocketOptions {
  url: string
  onOpen?: (event: Event) => void
  onMessage?: (event: MessageEvent) => void
  onError?: (event: Event) => void
  onClose?: (event: CloseEvent) => void
  reconnectInterval?: number
  maxReconnectAttempts?: number
  heartbeatInterval?: number
  heartbeatMessage?: string | object
  debug?: boolean
}

export class WebSocketService {
  private socket: WebSocket | null = null
  private reconnectAttempts = 0
  private reconnectInterval: number
  private maxReconnectAttempts: number
  private heartbeatInterval: number
  private heartbeatMessage: string
  private heartbeatTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private forceClosed = false
  private url: string
  private onOpenCallback?: (event: Event) => void
  private onMessageCallback?: (event: MessageEvent) => void
  private onErrorCallback?: (event: Event) => void
  private onCloseCallback?: (event: CloseEvent) => void
  private debug: boolean

  constructor(options: WebSocketOptions) {
    this.url = options.url
    this.onOpenCallback = options.onOpen
    this.onMessageCallback = options.onMessage
    this.onErrorCallback = options.onError
    this.onCloseCallback = options.onClose
    this.reconnectInterval = options.reconnectInterval || 3000
    this.maxReconnectAttempts = options.maxReconnectAttempts || 5
    this.heartbeatInterval = options.heartbeatInterval || 30000
    this.heartbeatMessage =
      typeof options.heartbeatMessage === 'object'
        ? JSON.stringify(options.heartbeatMessage)
        : options.heartbeatMessage || JSON.stringify({ type: 'heartbeat' })
    this.debug = options.debug || false
  }

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.forceClosed = false
        this.socket = new WebSocket(this.url)

        this.socket.onopen = (event) => {
          this.log('WebSocket connection opened')
          this.reconnectAttempts = 0
          this.startHeartbeat()

          if (this.onOpenCallback) {
            this.onOpenCallback(event)
          }

          resolve()
        }

        this.socket.onmessage = (event) => {
          if (this.onMessageCallback) {
            this.onMessageCallback(event)
          }
        }

        this.socket.onerror = (event) => {
          this.log('WebSocket error:', event)

          if (this.onErrorCallback) {
            this.onErrorCallback(event)
          }

          reject(new Error('WebSocket connection error'))
        }

        this.socket.onclose = (event) => {
          this.log(`WebSocket connection closed: ${event.code} ${event.reason}`)
          this.stopHeartbeat()

          if (this.onCloseCallback) {
            this.onCloseCallback(event)
          }

          // 4000-4999 is application-defined and never sent by infrastructure:
          // the server uses it for refusals that retrying cannot fix.
          if (event.code >= 4000 && event.code <= 4999) {
            this.log(`Not reconnecting: server refused with ${event.code}`)
            return
          }

          if (!this.forceClosed) {
            this.attemptReconnect()
          }
        }
      } catch (error) {
        this.log('Failed to create WebSocket:', error)
        reject(error)
      }
    })
  }

  public disconnect(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.forceClosed = true
      this.stopHeartbeat()

      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }

      if (this.socket) {
        // Store reference to the onclose callback
        const originalOnClose = this.socket.onclose

        // Replace with our callback that resolves the promise
        this.socket.onclose = (event) => {
          this.log('WebSocket connection closed by client')

          // Call the original onclose if it exists
          if (originalOnClose && typeof originalOnClose === 'function') {
            // Add a null check before calling
            if (this.socket) {
              originalOnClose.call(this.socket, event)
            }
          }

          this.socket = null
          resolve()
        }

        if (
          this.socket.readyState === WebSocketReadyState.OPEN ||
          this.socket.readyState === WebSocketReadyState.CONNECTING
        ) {
          this.socket.close()
        } else {
          this.socket = null
          resolve()
        }
      } else {
        resolve()
      }
    })
  }

  public send(message: string | object): boolean {
    if (!this.socket || this.socket.readyState !== WebSocketReadyState.OPEN) {
      this.log('Cannot send message, WebSocket is not connected')
      return false
    }

    try {
      const data =
        typeof message === 'string' ? message : JSON.stringify(message)
      this.socket.send(data)
      return true
    } catch (error) {
      this.log('Error sending message:', error)
      return false
    }
  }

  public getReadyState(): WebSocketReadyState {
    return this.socket ? this.socket.readyState : WebSocketReadyState.CLOSED
  }

  public isConnected(): boolean {
    return (
      this.socket !== null &&
      this.socket.readyState === WebSocketReadyState.OPEN
    )
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()

    if (this.heartbeatInterval > 0) {
      this.heartbeatTimer = setInterval(() => {
        if (this.isConnected()) {
          this.log('Sending heartbeat')
          this.send(this.heartbeatMessage)
        }
      }, this.heartbeatInterval)
    }
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  private attemptReconnect(): void {
    if (this.forceClosed) {
      return
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log(
        `Max reconnect attempts (${this.maxReconnectAttempts}) reached, giving up`
      )
      return
    }

    this.reconnectAttempts++
    // Exponential backoff, but capped so long outages keep retrying at a steady
    // cadence instead of ballooning to minutes between attempts.
    const MAX_RECONNECT_DELAY_MS = 15000
    const delay = Math.min(
      this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1),
      MAX_RECONNECT_DELAY_MS
    )

    this.log(
      `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`
    )

    this.reconnectTimer = setTimeout(() => {
      this.log(`Reconnecting... (attempt ${this.reconnectAttempts})`)
      this.connect().catch((error) => {
        this.log(`Reconnect attempt failed:`, error)
      })
    }, delay)
  }

  private log(...args: any[]): void {
    if (this.debug) {
      console.log(`[WebSocket:${new URL(this.url).pathname}]`, ...args)
    }
  }
}

// Factory function to create WebSocket endpoints
export function createWebSocket(
  path: string,
  params: Record<string, string>,
  options: Omit<WebSocketOptions, 'url'> = {}
): WebSocketService {
  // Build URL with parameters
  const urlParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      urlParams.append(key, value)
    }
  })

  const url = `${WEBSOCKET_URL}${path}?${urlParams.toString()}`

  return new WebSocketService({
    url,
    ...options,
  })
}
